"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { BellRing, Clock, ListChecks } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/features/ai-job-match";

// ============================================================================
// HERO TABS
// ============================================================================

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "ranked-matches",
    name: "Ranked Matches",
    title: "Your Best-Fit Jobs, Listed First",
    description:
      "Sort by Recommended and the roles that suit your profile rise to the top, each with a match score and the reasons it fits.",
    imageSrcUrl: `${IMG}/hero-ranked-matches.png`,
    primaryActionText: "Find My Matches",
    primaryActionLink: "/jobs",
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "smart-filters",
    name: "Smart Filters",
    title: "Filter the Way You Actually Search",
    description:
      "Narrow results by work mode, experience, department, salary band and company type. Every filter shows how many jobs it leaves you with.",
    imageSrcUrl: `${IMG}/hero-smart-filters.png`,
    primaryActionText: "Find My Matches",
    primaryActionLink: "/jobs",
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "jobsgpt",
    name: "JobsGPT",
    title: "Review Matches One Swipe at a Time",
    description:
      "JobsGPT shows one role at a time with the details that matter. Swipe right to apply, swipe left to pass, and get through a shortlist in minutes.",
    imageSrcUrl: `${IMG}/hero-jobsgpt.png`,
    primaryActionText: "Try JobsGPT",
    primaryActionLink: "/jobs",
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

// ============================================================================
// FEATURE GROUP 1 (dark)
// ============================================================================

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "fresh-postings",
    title: "Fresh Postings First",
    subtitle: "APPLY WHILE IT COUNTS",
    description:
      "Every listing shows when it was posted. Sort and filter toward recent roles, where early applicants are read before the pile builds up.",
    benefits: [
      "Posting date on every job",
      "Recent roles surfaced first",
      "Older listings clearly marked",
      "New jobs added every hour",
    ],
    primaryCTA: "See Today's Jobs",
    primaryLink: "/jobs",
    imageSrcUrl: `${IMG}/fresh-postings.png`,
    icon: <Clock className="w-4 h-4" />,
    stats: "Updated hourly",
  },
  {
    id: "job-alerts",
    title: "Alerts for New Matches",
    subtitle: "SEND ME JOBS LIKE THESE",
    description:
      "Turn any search into an alert with one click. New roles that fit it arrive in your inbox, so you hear about them the day they go live.",
    benefits: [
      "One-click alert from any search",
      "Only roles that match",
      "Daily email digest",
      "Every alert links straight to the job",
    ],
    primaryCTA: "Create a Job Alert",
    primaryLink: "/jobs",
    secondaryCTA: "See How It Works",
    secondaryLink: "/how-to-use",
    imageSrcUrl: `${IMG}/job-alerts.png`,
    icon: <BellRing className="w-4 h-4" />,
    stats: "One-click alerts",
    orientation: "reversed",
  },
];

// ============================================================================
// FEATURE GROUP 2 (light)
// ============================================================================

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "one-click-track",
    title: "One Click to Track",
    subtitle: "FROM LISTING TO PIPELINE",
    description:
      "Hit Track on any job and it moves into JobTracker AI with the role, company and link filled in. Save the rest to review later.",
    benefits: [
      "Track button on every listing",
      "Tracker cards filled in for you",
      "Saved jobs kept in one list",
      "Pipeline view from saved to offer",
    ],
    primaryCTA: "Start Tracking Jobs",
    primaryLink: "/jobs",
    secondaryCTA: "Explore JobTracker AI",
    secondaryLink: "/features/jobtracker-ai",
    imageSrcUrl: `${IMG}/one-click-track.png`,
    icon: <ListChecks className="w-4 h-4" />,
    stats: "Works with JobTracker AI",
  },
];

// ============================================================================
// TESTIMONIALS (replace with real user quotes before publishing)
// ============================================================================

const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "Sorting by Recommended put three roles at the top that I would never have searched for. I interviewed at two of them.",
    name: "Neha V.",
    role: "Frontend Developer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "Seeing the posting date on every job changed how I search. I apply to anything under three days old first and leave the rest for later.",
    name: "Karan B.",
    role: "Sales Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "The alert lands every morning with five or six jobs that actually fit. Tracking one takes a click, so nothing slips through.",
    name: "Ananya T.",
    role: "Data Scientist",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=600&h=800&fit=crop&q=80",
  },
];

// ============================================================================
// PAGE
// ============================================================================

export default function JobBoardAIPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Fewer Listings.",
            titleHighlight: "Better Matches.",
            subtitle:
              "AI Job Match ranks open roles by how well they fit your skills, experience and preferences. See how fresh every posting is, filter the way you actually search, and track a job in one click.",
            ctaText: "Find My Matches",
            ctaLink: "/jobs",
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Spend Time on Jobs Worth It.",
            sectionSubtitle:
              "Fresh postings, saved searches and alerts that keep your shortlist short.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Shortlists, Not Search Fatigue",
            subtitle: "From job seekers who search smarter with AI Job Match",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About AI Job Match",
            subtitle: "How matching works and how to get the most from it.",
            faqs,
            ctaTitle: "Ready to see your matches?",
            ctaText: "Find My Matches",
            ctaLink: "/jobs",
          },
        },
      ]}
    />
  );
}
